import React, { useState } from 'react';
import { SongAdProject } from '../../services/api/songAdService';
import { TaskyButton, TaskyCard, TaskyEmptyState, TaskyAlert } from '../tasky';
import { Music2, Loader2, ChevronRight, ArrowRight, SlidersHorizontal, RefreshCw, Clock } from 'lucide-react';
import SongPlayer from './SongPlayer';

/**
 * ステップ②: 楽曲。
 *
 * Lyria でかな歌詞を歌わせた楽曲を生成し、SongPlayer（波形/シーク/音量つきミニプレーヤー）で試聴する。
 * v3で追加: 歌詞タイミング表示（§9.4）・「この曲で進める/同条件で再生成/条件を変更」の3導線。
 *
 * v4 F8: 歌詞タイミングの取得元を `timeline.captions`（captions/init。シーン存在必須）から
 * `project.lyric_timings`（POST /song-ads/{id}/timings。scenesに依存しない）に変更。
 * これにより「この曲で進める」押下時に自動取得でき、この画面の「取得する」ボタンも
 * シーン未生成のうちから押せる（v3の hasScenes ガードは廃止 = ②の原因分析どおり）。
 */

interface SongStepProps {
  project: SongAdProject;
  isGenerating: boolean;
  onGenerate: () => Promise<void>;
  /** 「この曲で進める」。歌詞タイミング未取得なら自動取得してから次工程（ストーリーボード）へ進む */
  onProceed?: () => void;
  /** 「条件を変更する」。設定編集モーダルを開く（作成設定へ戻る） */
  onEditSettings?: () => void;
  /** 歌詞行ごとのタイミング取得（POST /song-ads/{id}/timings）。song_ready以降ならシーン未生成でも呼べる */
  onFetchTimings?: () => Promise<void>;
  isFetchingTimings?: boolean;
}

const formatSec = (seconds: number): string => `${seconds.toFixed(1)}s`;

const SongStep: React.FC<SongStepProps> = ({
  project,
  isGenerating,
  onGenerate,
  onProceed,
  onEditSettings,
  onFetchTimings,
  isFetchingTimings = false,
}) => {
  const [timingOpen, setTimingOpen] = useState(false);
  const canGenerate = !!project.lyrics_fixed_at;
  const isProcessing = isGenerating || project.status === 'song_generating';
  const timings = project.lyric_timings ?? [];

  if (!canGenerate) {
    return (
      <TaskyEmptyState
        icon={<Music2 />}
        title="先に歌詞をフィックスしてください"
        description="「① 歌詞」で保存すると楽曲を生成できます"
      />
    );
  }

  return (
    <div className="space-y-5">
      <TaskyCard variant="warm" padding="md">
        <h3 className="text-sm font-semibold text-dark mb-3">楽曲</h3>

        {project.song_error_message && project.status === 'song_failed' && (
          <TaskyAlert tone="error" className="mb-3">
            曲生成に失敗しました: {project.song_error_message}
          </TaskyAlert>
        )}

        {isProcessing ? (
          <div className="flex flex-col items-center py-8 text-gray-400">
            <Loader2 className="w-7 h-7 animate-spin mb-2" />
            <span className="text-xs">楽曲を生成中です（数分かかることがあります）。完了すると自動で表示されます。</span>
          </div>
        ) : project.song_url ? (
          <div className="space-y-2">
            <SongPlayer src={project.song_url} />
            {project.song_duration_sec != null && (
              <p className="text-xs text-gray-500">尺: 約{Math.round(project.song_duration_sec)}秒</p>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center py-8 text-gray-400">
            <Music2 className="w-9 h-9 mb-2 opacity-40" />
            <span className="text-xs">まだ楽曲がありません</span>
          </div>
        )}

        <div className="flex items-center gap-3 mt-4">
          <TaskyButton onClick={onGenerate} isLoading={isProcessing} disabled={isProcessing}>
            <Music2 className="w-4 h-4" />
            {project.song_url ? '再生成する' : '曲を生成する'}
          </TaskyButton>
          <span className="text-xs text-gray-400">約100クレジット消費します</span>
        </div>

        {/* 歌詞タイミング（§9.4。v4 F8: シーン未生成でも取得できる） */}
        {project.song_url && !isProcessing && (
          <div className="mt-4 pt-4 border-t border-warm-border">
            <button
              type="button"
              onClick={() => setTimingOpen((v) => !v)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ChevronRight className={`w-4 h-4 transition-transform ${timingOpen ? 'rotate-90' : ''}`} />
              <Clock className="w-3.5 h-3.5" />
              <span>歌詞タイミング{timings.length > 0 ? '（取得済み）' : ''}</span>
            </button>
            {timingOpen && (
              <div className="mt-2 bg-white border border-warm-border rounded-lg p-3">
                {timings.length > 0 ? (
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {timings.map((t) => (
                      <div
                        key={t.index}
                        className={`flex items-center gap-3 text-[12px] ${t.matched ? '' : 'opacity-40'}`}
                        title={t.matched ? undefined : '歌唱が確認できなかった行（前後から時刻を補間）'}
                      >
                        <span className="text-gray-400 tabular-nums w-24 shrink-0">
                          {formatSec(t.start)} – {formatSec(t.end)}
                        </span>
                        <span className="text-gray-700 truncate">{t.text}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-gray-500">
                      まだ取得していません。「この曲で進める」を押すと自動で取得されます（ここで先に取得することもできます）。
                    </p>
                    {onFetchTimings && (
                      <TaskyButton size="sm" variant="outline" onClick={onFetchTimings} isLoading={isFetchingTimings}>
                        取得する
                      </TaskyButton>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </TaskyCard>

      {project.song_url && !isProcessing && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-2">
            {onEditSettings && (
              <TaskyButton variant="outline" size="sm" onClick={onEditSettings}>
                <SlidersHorizontal className="w-3.5 h-3.5" />
                条件を変更する
              </TaskyButton>
            )}
            <TaskyButton variant="outline" size="sm" onClick={onGenerate} isLoading={isProcessing}>
              <RefreshCw className="w-3.5 h-3.5" />
              同じ条件で再生成
            </TaskyButton>
          </div>
          {onProceed && (
            <TaskyButton onClick={onProceed} isLoading={isFetchingTimings} disabled={isFetchingTimings}>
              この曲で進める
              <ArrowRight className="w-4 h-4" />
            </TaskyButton>
          )}
        </div>
      )}
    </div>
  );
};

export default SongStep;
